/**
 * Visitor cookie consent for PUBLIC pages, shared by everything that loads a
 * third-party tag: the per-page tags (TrackingScripts), the site-wide GTM
 * container (useGTM) and paid-media conversions (leadTrackingDispatcher).
 *
 * One stored choice, one event. The banner writes the choice with
 * setConsent(); anything waiting to load listens with onConsentChange().
 * "No choice yet" and "denied" both mean nothing third-party loads.
 */

export const CONSENT_KEY = 'linkhub-consent';
const CONSENT_EVENT = 'linkhub:consent';

const GRANTED = {
  ad_storage: 'granted', analytics_storage: 'granted',
  ad_user_data: 'granted', ad_personalization: 'granted',
};
const DENIED = {
  ad_storage: 'denied', analytics_storage: 'denied',
  ad_user_data: 'denied', ad_personalization: 'denied',
};

/** 'granted' | 'denied' | null (not asked yet, or storage unavailable). */
export function getConsent() {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;   // private mode / blocked storage: ask again
  }
}

export function hasConsent() {
  return getConsent() === 'granted';
}

function gtag() {
  const w = window;
  w.dataLayer = w.dataLayer || [];
  w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };
  return w.gtag;
}

/** Google Consent Mode v2 default: DENIED. Call before any Google tag exists. */
export function applyConsentDefault() {
  const set = gtag();
  const already = window.dataLayer.some((a) => a && a[0] === 'consent' && a[1] === 'default');
  if (!already) set('consent', 'default', DENIED);
}

/** Tell Google tags consent was given (after applyConsentDefault). */
export function grantGoogleConsent() {
  gtag()('consent', 'update', GRANTED);
}

/** Record the visitor's choice and notify every listener on the page. */
export function setConsent(granted) {
  const value = granted ? 'granted' : 'denied';
  try { localStorage.setItem(CONSENT_KEY, value); } catch { /* still applies for this view */ }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { value } }));
}

/** Subscribe to the visitor's choice; returns an unsubscribe function. */
export function onConsentChange(cb) {
  const handler = (e) => cb(e.detail?.value);
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}
